import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, type Profile, type User } from './api';

type Status = 'loading' | 'authed' | 'guest';

interface AuthState {
  status: Status;
  user: User | null;
  profile: Profile | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);

  const refresh = useCallback(async () => {
    try {
      const { user, profile } = await api.me();
      setUser(user);
      setProfile(profile);
      setStatus(user ? 'authed' : 'guest');
    } catch {
      setUser(null);
      setProfile(null);
      setStatus('guest');
    }
  }, []);

  const signOut = useCallback(async () => {
    await api.logout().catch(() => {});
    setUser(null);
    setProfile(null);
    setStatus('guest');
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ status, user, profile, refresh, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
